import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { logger } from '../../services/logger';
import {
  isSessionOnlyAuth,
  recordSessionActivity,
  getLastSessionActivity,
  clearSessionAuthFlags,
  hasSessionCookie,
  setSessionCookie,
  getSessionBroadcastChannel
} from '../../firebase/authUtils';
import {
  handleBroadcastMessage,
  setupCrossTabProbe,
  createVisibilityHandler,
  createStorageHandler,
  bindWindowListeners
} from './sessionTimeoutUtils';

export const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000;
export const DEFAULT_WARNING_MS = 28 * 60 * 1000;
export const DEFAULT_PROBE_MS = 300;
export const THROTTLE_MS = 10 * 1000;

const createActivityUpdater = ({ showWarning, lastRecordedRef, channelRef }) => () => {
  if (showWarning || !isSessionOnlyAuth()) return;
  const currentNow = Date.now();
  if (currentNow - lastRecordedRef.current > THROTTLE_MS) {
    recordSessionActivity();
    lastRecordedRef.current = currentNow;
    if (channelRef.current) {
      try {
        channelRef.current.postMessage({ type: 'SESSION_ACTIVITY', timestamp: currentNow });
      } catch { /* ignore */ }
    }
  }
};

export const useSessionTimeout = ({
  user,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  warningMs = DEFAULT_WARNING_MS,
  checkIntervalMs = 10 * 1000,
  probeTimeoutMs = DEFAULT_PROBE_MS
}) => {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const lastRecordedRef = useRef(0);
  const channelRef = useRef(null);

  const handleSignOut = useCallback(async (reason = 'timeout') => {
    setShowWarning(false);
    if (reason === 'timeout') {
      try {
        window.sessionStorage.setItem('tas_timeout_reason', 'timeout');
      } catch { /* ignore */ }
    }
    if (channelRef.current) {
      try {
        channelRef.current.postMessage({ type: 'SESSION_SIGN_OUT', reason });
      } catch { /* ignore */ }
    }
    clearSessionAuthFlags();
    try {
      await signOut(auth);
    } catch (err) {
      logger.warn('Error signing out during session timeout:', err);
    }
    navigate(`/login?reason=${reason}`);
  }, [navigate]);

  const handleStaySignedIn = useCallback(() => {
    recordSessionActivity();
    setSessionCookie();
    lastRecordedRef.current = Date.now();
    setShowWarning(false);
    if (channelRef.current) {
      try {
        channelRef.current.postMessage({ type: 'SESSION_EXTEND' });
      } catch { /* ignore */ }
    }
  }, []);

  const checkTimeout = useCallback(() => {
    if (!isSessionOnlyAuth()) return setShowWarning(false);
    const lastActivity = getLastSessionActivity();
    if (!lastActivity) return;
    const elapsed = Date.now() - lastActivity;

    if (elapsed >= timeoutMs) {
      handleSignOut('timeout');
    } else if (elapsed >= warningMs) {
      const remainingMs = Math.max(0, timeoutMs - elapsed);
      setSecondsRemaining(Math.ceil(remainingMs / 1000));
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  }, [timeoutMs, warningMs, handleSignOut]);

  useEffect(() => {
    if (!user || !isSessionOnlyAuth()) {
      setShowWarning(false);
      return;
    }
    if (!hasSessionCookie()) {
      handleSignOut('browser_closed');
      return;
    }
    const existing = getLastSessionActivity();
    if (!existing || (Date.now() - existing >= timeoutMs)) {
      recordSessionActivity();
      setSessionCookie();
    }
    lastRecordedRef.current = Date.now();

    const probeState = { timerId: null };
    const channel = getSessionBroadcastChannel();
    channelRef.current = channel;
    if (channel) {
      channel.onmessage = (event) => handleBroadcastMessage({
        data: event.data,
        channel,
        probeState,
        onSignOut: (reason) => {
          if (reason === 'timeout') {
            try {
              window.sessionStorage.setItem('tas_timeout_reason', 'timeout');
            } catch { /* ignore */ }
          }
          setShowWarning(false);
          clearSessionAuthFlags();
          navigate(`/login?reason=${reason}`);
        },
        onExtend: () => {
          lastRecordedRef.current = Date.now();
          setShowWarning(false);
        }
      });
    }

    setupCrossTabProbe({ onSignOut: handleSignOut });

    const updateActivity = createActivityUpdater({ showWarning, lastRecordedRef, channelRef });
    const intervalId = setInterval(checkTimeout, checkIntervalMs);
    const unbind = bindWindowListeners({
      updateActivity,
      handleVisibilityOrFocus: createVisibilityHandler({ timeoutMs, handleSignOut, checkTimeout, lastRecordedRef }),
      handleStorageChange: createStorageHandler({ checkTimeout, lastRecordedRef, setShowWarning })
    });

    return () => {
      unbind();
      clearInterval(intervalId);
      if (probeState.timerId) clearTimeout(probeState.timerId);
      if (channel) channel.close();
    };
  }, [user, showWarning, timeoutMs, checkIntervalMs, probeTimeoutMs, handleSignOut, navigate, checkTimeout]);

  useEffect(() => {
    if (!showWarning) return;
    const interval = setInterval(() => {
      if (!isSessionOnlyAuth()) return setShowWarning(false);
      const lastActivity = getLastSessionActivity();
      if (!lastActivity) return;
      const elapsed = Date.now() - lastActivity;
      const remaining = Math.max(0, timeoutMs - elapsed);
      setSecondsRemaining(Math.ceil(remaining / 1000));
      if (remaining <= 0) handleSignOut('timeout');
    }, 1000);
    return () => clearInterval(interval);
  }, [showWarning, timeoutMs, handleSignOut]);

  return {
    showWarning,
    secondsRemaining,
    handleStaySignedIn,
    handleSignOut,
  };
};
